import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as d3 from "d3";
import { RiskItemRow } from "./DataControlRiskTable";

const DOT_HIT_RADIUS = 5; // collision radius used by the simulation

// severity already has an established color language everywhere else in
// this app (SEVERITY_TONE → red/amber/blue), so groups here reuse those
// same tones instead of a generic open-ended palette.
const SEVERITY_COLOR = {
  CRITICAL: "#5c1420",
  HIGH: "#9b2020",
  MEDIUM: "#b45309",
  LOW: "#1e3a8a",
  UNKNOWN: "#4b5563",
};

const seededRandom = (seed) => {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

// groups by severity — HIGH / MEDIUM / LOW, always populated by the
// backend, unlike sensitivity which is currently null for every row
function normalizeGroup(item) {
  return (item.severity || "UNKNOWN").toUpperCase();
}

// arranges N group anchors evenly around a circle, so any number of
// severity categories lays out without hardcoded positions
function arrangeAnchors(keys, width, height) {
  const cx = width / 2;
  const cy = height / 2;
  const R = Math.min(width, height) * (keys.length <= 1 ? 0 : 0.28);

  return keys.reduce((acc, key, i) => {
    const angle = (i / keys.length) * Math.PI * 2 - Math.PI / 2;
    acc[key] = { x: cx + R * Math.cos(angle), y: cy + R * Math.sin(angle) };
    return acc;
  }, {});
}

// how far dots should be allowed to spread from their group's anchor —
// scaled to canvas size and group count so a single group fans out
// across most of the stage instead of huddling in a tiny point, while
// multiple groups still leave room between each other
function spreadRadiusFor(keyCount, width, height) {
  const base = Math.min(width, height);
  if (keyCount <= 1) return base * 0.38;
  if (keyCount === 2) return base * 0.24;
  return base * 0.17;
}

// ── single dot ── one flagged file. Hover shows a detail tooltip (mirrors
// the old ClusterDot); click opens the full RiskItemRow card. ─────────────
function ScatterDot({ node, color, dimmed, onHover, onLeave, onClick }) {
  return (
    <span
      className="fsc-sens-scatter__dot"
      style={{
        left: node.x,
        top: node.y,
        background: color,
        opacity: dimmed ? 0.15 : 0.85,
      }}
      onMouseEnter={(e) => onHover(node, e)}
      onMouseLeave={onLeave}
      onClick={(e) => onClick(node, e)}
    />
  );
}

export default function DataControlSensitivityScatter({
  items = [],
  categoryId,
  appliedActions,
  onAction,
  dispatch,
}) {
  const containerRef = useRef(null);
  const worldRef = useRef(null);

  const [size, setSize] = useState([0, 0]);
  const [nodes, setNodes] = useState([]);
  const [groupAnchors, setGroupAnchors] = useState({});
  const [activeFilter, setActiveFilter] = useState(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });

  const [hover, setHover] = useState(null); // { node, x, y }
  const [zoomedItem, setZoomedItem] = useState(null); // { node, origin }

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width && height) setSize([width, height]);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const sensitivityKeys = useMemo(() => {
    const set = new Set(items.map((it) => normalizeGroup(it)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  console.log(sensitivityKeys,"fdhfg")

  const sensitivityColors = useMemo(
    () =>
      sensitivityKeys.reduce((acc, key) => {
        acc[key] = SEVERITY_COLOR[key] || "#4b5563";
        return acc;
      }, {}),
    [sensitivityKeys],
  );

  // layout pass — real per-item nodes, force-pulled toward their
  // severity's anchor point and repelled from each other so they swarm
  // organically instead of overlapping. Frozen (ticked upfront, not live)
  // so it doesn't keep recomputing on every hover/click re-render.
  useEffect(() => {
    if (!size[0] || items.length === 0) {
      setNodes([]);
      setGroupAnchors({});
      return;
    }

    const anchors = arrangeAnchors(sensitivityKeys, size[0], size[1]);
    const spread = spreadRadiusFor(sensitivityKeys.length, size[0], size[1]);

    const simNodes = items.map((it, i) => {
      const group = normalizeGroup(it);
      const anchor = anchors[group];
      // sunflower-style seed placement so dots start already spread
      // across the group's radius, not clumped at the anchor point
      const r = spread * Math.sqrt(seededRandom(i * 97.31));
      const angle = seededRandom(i * 131.7) * Math.PI * 2;
      return {
        ...it,
        _group: group,
        x: anchor.x + r * Math.cos(angle),
        y: anchor.y + r * Math.sin(angle),
      };
    });

    const simulation = d3
      .forceSimulation(simNodes)
      .force("x", d3.forceX((d) => anchors[d._group].x).strength(0.04))
      .force("y", d3.forceY((d) => anchors[d._group].y).strength(0.04))
      .force("charge", d3.forceManyBody().strength(-4))
      .force(
        "collision",
        d3.forceCollide().radius(DOT_HIT_RADIUS).strength(0.9),
      )
      .stop();

    for (let i = 0; i < 300; i++) simulation.tick();

    setNodes(simNodes);
    setGroupAnchors(anchors);
  }, [items, size, sensitivityKeys]);

  // pan/zoom — bound directly to the container div; the resulting
  // transform is applied via CSS to the "world" wrapper, so dots stay
  // real interactive DOM elements throughout (not canvas/SVG).
  useEffect(() => {
    if (!containerRef.current) return;

    const zoomBehavior = d3
      .zoom()
      .scaleExtent([0.6, 3])
      .on("zoom", (event) => {
        setTransform({
          x: event.transform.x,
          y: event.transform.y,
          k: event.transform.k,
        });
      });

    d3.select(containerRef.current).call(zoomBehavior);
  }, []);

  const captureOrigin = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: `${((e.clientX - rect.left) / rect.width) * 100}%`,
      y: `${((e.clientY - rect.top) / rect.height) * 100}%`,
    };
  };

  const handleHover = (node, e) => {
    const rect = containerRef.current.getBoundingClientRect();
    setHover({
      node,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleDotClick = (node, e) => {
    e.stopPropagation();
    setZoomedItem({ node, origin: captureOrigin(e) });
    setHover(null);
  };

  const closeZoom = () => setZoomedItem(null);

  if (items.length === 0) {
    return (
      <p className="fsc-fullscreen__no-log">
        No severity data available for this category.
      </p>
    );
  }

  return (
    <div className="fsc-sens-scatter">
      <div className="fsc-sens-scatter__pills">
        {sensitivityKeys.map((key) => (
          <button
            key={key}
            className={`fsc-sens-scatter__pill ${
              activeFilter === key ? "fsc-sens-scatter__pill--active" : ""
            }`}
            style={{
              borderColor: sensitivityColors[key],
              color:
                activeFilter && activeFilter !== key
                  ? "#9aa4b8"
                  : sensitivityColors[key],
            }}
            onClick={() =>
              setActiveFilter((prev) => (prev === key ? null : key))
            }
          >
            {key}
          </button>
        ))}
      </div>

      <div className="fsc-sens-scatter__stage" ref={containerRef}>
        <div
          className="fsc-sens-scatter__world"
          ref={worldRef}
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.k})`,
          }}
        >
          {Object.entries(groupAnchors).map(([key, anchor]) => (
            <span
              key={key}
              className="fsc-sens-scatter__label"
              style={{
                left: anchor.x,
                top: anchor.y,
                color: sensitivityColors[key],
                opacity: activeFilter && activeFilter !== key ? 0.3 : 1,
              }}
            >
              {key}
            </span>
          ))}

          {nodes.map((node, i) => (
            <ScatterDot
              key={node.id || i}
              node={node}
              color={sensitivityColors[node._group]}
              dimmed={!!activeFilter && activeFilter !== node._group}
              onHover={handleHover}
              onLeave={() => setHover(null)}
              onClick={handleDotClick}
            />
          ))}
        </div>

        {hover && !zoomedItem && (
          <div
            className="fsc-sens-scatter__tooltip"
            style={{ left: hover.x + 16, top: hover.y }}
          >
            <div className="fsc-cluster__tooltip-head">
              <span
                className={`fsc-risk-card__priority fsc-risk-card__priority--${
                  hover.node.severity === "HIGH" ? "red" : "blue"
                }`}
              >
                {hover.node.severity}
              </span>
            </div>
            <p className="fsc-cluster__tooltip-title">{hover.node.title}</p>
            <p className="fsc-cluster__tooltip-sub">{hover.node.subtitle}</p>
            <p className="fsc-cluster__tooltip-desc">
              {hover.node.aiSummary || hover.node.description}
            </p>
          </div>
        )}

        <p className="fsc-sens-scatter__hint">
          Hover a dot to preview · click to open
        </p>
      </div>

      {/* full card zoom — no explicit Back button; clicking anywhere
          outside the card (the overlay backdrop) closes it ── */}
      {zoomedItem && (
        <div className="fsc-cluster__item-zoom-overlay" onClick={closeZoom}>
          <div
            className="fsc-cluster__item-zoom"
            style={{
              transformOrigin: `${zoomedItem.origin.x} ${zoomedItem.origin.y}`,
            }}
          >
            <div
              className="fsc-cluster__item-zoom-card"
              onClick={(e) => e.stopPropagation()}
            >
              <RiskItemRow
                item={zoomedItem.node}
                categoryId={categoryId}
                appliedActions={appliedActions}
                onAction={onAction}
                dispatch={dispatch}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}